import {arrayToSet} from "./atUtils";
import _ from "underscore";
import CustomerStore from "./store/CustomerStore";
import ProjectStore from "./store/ProjectStore";

/**
 * Represents customer/project selection, which is widely used in reports and filters.
 * @Deprecated contains usages of old entities store
 */
export default class CustomerProjectSelection {
    /**
     * @param {String} [qualifier] Selection qualifier, can be "ALL", "ALL_ACTIVE" or "SELECTED" (see CustomerProjectSelection.Qualifier enum). Defaults to "ALL".
     * @param {Array} [customers] IDs of selected customers. Ignored if 'qualifier' param is "ALL" or "ALL_ACTIVE".
     * @param {Array} [projects] IDs of selected projects. Ignored if 'qualifier' param is "ALL" or "ALL_ACTIVE".
     * @param {Boolean} [activeOnly] Indicates if selection should only include active entities (NB! inactive entities will not count even if they are specified in customers/projects arrays!)
     */
    constructor(qualifier, customers, projects, activeOnly = false)
    {
        this.qualifier = qualifier || CustomerProjectSelection.Qualifier.ALL;

        this._all = this.qualifier === CustomerProjectSelection.Qualifier.ALL || this.qualifier === CustomerProjectSelection.Qualifier.ALL_ACTIVE;
        this.activeOnly = this.qualifier === CustomerProjectSelection.Qualifier.ALL_ACTIVE || activeOnly;

        if (!this._all)
        {
            this.customers = customers || [];
            this.projects = projects || [];
        }
        else
        {
            this.customers = [];
            this.projects = [];
        }

        this._customersSet = arrayToSet(this.customers);
        this._projectsSet = arrayToSet(this.projects);
    }

    /**
     * Makes selection instance converting it from plain object form, commonly used in project.
     * @param {Object} cpSelectionObject
     * @param {String} cpSelectionObject.qualifier Selection qualifier.
     * @param {Array} cpSelectionObject.customers IDs of selected customers.
     * @param {Array} cpSelectionObject.projects IDs of selected projects.
     * @returns {CustomerProjectSelection}
     */
    static fromPlainObject(cpSelectionObject)
    {
        return new CustomerProjectSelection(cpSelectionObject.qualifier, cpSelectionObject.customers, cpSelectionObject.projects);
    }

    /**
     * Determines if customer is included in this selection.
     * @param {Customer} customer
     * @param {boolean} [transitive]
     * @returns {boolean}
     */
    includesCustomer(customer, transitive = false)
    {
        if (!customer)
            return false;
        if (this.activeOnly && customer.archived)
            return false;
        else if (this._all)
            return true;
        else
            return !!this._customersSet[customer.id] || transitive && _.any(ProjectStore.getByCustomer(customer.id), p => this.includesProject(p));
    }

    /**
     * Determines if project is included in this selection.
     * @param {Project} project
     * @returns {boolean}
     */
    includesProject(project)
    {
        if (!project)
            return false;
        if (this.activeOnly && project.archived)
            return false;
        else if (this._all)
            return true;
        else
            return !!this._projectsSet[project.id] || !!this._customersSet[project.customerId];
    }

    /**
     * Determines if this selection is empty and thus guaranteed to not include any customer/project
     * (i.e. has "SELECTED" qualifier and doesn't specify any customer/project ID).
     * @returns {boolean}
     */
    isEmpty()
    {
        return !this._all && this.customers.length === 0 && this.projects.length === 0;
    }

    /**
     * Determines if this selection is equal to another.
     * @param {CustomerProjectSelection} otherSelection
     * @return {boolean}
     */
    equals(otherSelection)
    {
        if (otherSelection && this.qualifier === otherSelection.qualifier)
        {
            return this === otherSelection ||
                   this._all ||
                   this.customers.length === otherSelection.customers.length &&
                   _.all(otherSelection.customers, c => this._customersSet[c]) &&
                   this.projects.length === otherSelection.projects.length &&
                   _.all(otherSelection.projects, p => this._projectsSet[p]);
        }

        return false;
    }

    /**
     * Intersects this selection with other, returning selection, which only includes customers/projects from both.
     * @param {CustomerProjectSelection} otherSelection
     * @returns {CustomerProjectSelection}
     */
    intersect(otherSelection)
    {
        let intersectionQualifier;
        if (this.qualifier === CustomerProjectSelection.Qualifier.ALL)
        {
            intersectionQualifier = otherSelection.qualifier;
        }
        else if (this.qualifier === CustomerProjectSelection.Qualifier.ALL_ACTIVE)
        {
            if (otherSelection.qualifier === CustomerProjectSelection.Qualifier.ALL)
                intersectionQualifier = this.qualifier;
            else
                intersectionQualifier = otherSelection.qualifier;
        }
        else if (this.qualifier === CustomerProjectSelection.Qualifier.SELECTED)
        {
            intersectionQualifier = this.qualifier;
        }

        const intersectionCustomersSet = {};
        this.customers.forEach(customerId =>
        {
            const customer = CustomerStore.get(customerId);
            if (customer && otherSelection.includesProject(customer))
                intersectionCustomersSet[customer.id] = true;
        });
        otherSelection.customers.forEach(customerId =>
        {
            const customer = CustomerStore.get(customerId);
            if (customer && this.includesCustomer(customer))
                intersectionCustomersSet[customer.id] = true;
        });
        const intersectionCustomers = _.keys(intersectionCustomersSet);

        const intersectionProjectsSet = {};
        this.projects.forEach(projectId =>
        {
            const project = ProjectStore.get(projectId);
            if (project && otherSelection.includesProject(project))
                intersectionProjectsSet[projectId] = true;
        });
        otherSelection.projects.forEach(projectId =>
        {
            const project = ProjectStore.get(projectId);
            if (project && this.includesProject(project))
                intersectionProjectsSet[projectId] = true;
        });
        const intersectionProjects = _.keys(intersectionProjectsSet);

        const intersectionActiveOnly = this.activeOnly || otherSelection.activeOnly;

        return new CustomerProjectSelection(intersectionQualifier, intersectionCustomers, intersectionProjects, intersectionActiveOnly);
    }

    toPlainObject()
    {
        return {
            qualifier: this.qualifier,
            activeOnly: this.activeOnly,
            customers: this.customers,
            projects: this.projects,
        }
    }
}

/**
 * Enums customer/project selection qualifiers.
 */
CustomerProjectSelection.Qualifier = {
    ALL: "ALL",
    ALL_ACTIVE: "ALL_ACTIVE",
    SELECTED: "SELECTED"
};

/**
 * Instance of customer/project selection with "ALL" qualifier.
 * @type {CustomerProjectSelection}
 */
CustomerProjectSelection.ALL = new CustomerProjectSelection();

/**
 * Instance of customer/project selection with "ALL_ACTIVE" qualifier.
 * @type {CustomerProjectSelection}
 */
CustomerProjectSelection.ALL_ACTIVE = new CustomerProjectSelection(CustomerProjectSelection.Qualifier.ALL_ACTIVE);


// WEBPACK FOOTER //
// ./web/js/CustomerProjectSelection.js